
import { Options, prop, Vue } from "vue-class-component";
import DetailEstimation from "./detail-estimation.vue";
import Skeleton from "@/app/ui/components/skeleton/index.vue";
import formatPriceRP from "@/app/infrastructures/misc/common-library/FormatPriceRP";
import formatPriceRM from "@/app/infrastructures/misc/common-library/FormatPriceRM";
import { configPrice } from "@/app/infrastructures/misc/common-library/modules/Price";
import { EstimationPrice } from "@/domain/entities/Booking";
import { DetailDiscount } from "@/domain/entities/Tariff";
import {
  CreateBookingV4,
  PaymentMethodBookingV4,
  PaymentMethodV4
} from "@/domain/entities/BookingV4";
import ChevronDown from "@/app/ui/assets/svg/chevron-down-new.svg";
import ChevronUp from "@/app/ui/assets/svg/chevron-up-new.svg";
import OverlayPanel from "primevue/overlaypanel";
import { formatByCountry } from "@/app/infrastructures/misc/Utils";

interface OverlayPanelRef {
  container: HTMLElement | null;
  show(event: { currentTarget: HTMLElement }): void;
  hide(): void;
}

class Props {
  modelValue = prop<CreateBookingV4>({
    default: new CreateBookingV4(),
    type: Object
  });
  estimationPrice = prop<EstimationPrice>({
    required: true,
    type: EstimationPrice
  });
  currencyPrefix = prop<string>({ default: "Rp", type: String });
  // GENESIS-21349 — destination currency for COD Amount / Biaya COD when
  // cross-country COD/DFOD is active; empty means "same as currencyPrefix"
  // (domestic, zero regression). Parent (create-booking-client.vue) passes
  // the already-resolved goodsPriceCurrencyPrefix/Code (GENESIS-21348).
  codCurrencyPrefix = prop<string>({ default: "", type: String });
  codCurrencyCode = prop<string>({ default: "", type: String });
  paymentMethod = prop<PaymentMethodBookingV4>({
    required: true,
    type: PaymentMethodBookingV4
  });
  // appliedVoucherCode = prop<string>({ default: "", type: String });
  itemPrice = prop<number | null>({ default: null });
  woodpacking = prop<boolean>({ default: false, type: Boolean });
  protectionChecked = prop<boolean>({ default: true, type: Boolean });
  isFtz = prop<boolean>({ default: false, type: Boolean });
  isLoading = prop<boolean>({ default: false, type: Boolean });
  isError = prop<boolean>({ default: false, type: Boolean });
  canOpenDetail = prop<boolean>({ default: true, type: Boolean });
  isPriceMasked = prop<boolean>({ default: false, type: Boolean });
  isRetail = prop<boolean>({ default: false, type: Boolean });
  isReverseJourney = prop<boolean>({ required: false, type: Boolean });
  isCrossCountryCod = prop<boolean>({ default: false, type: Boolean });
  hideCodReturnFee = prop<boolean>({ default: false, type: Boolean });
}

@Options({
  name: "TotalTarifSectionV4",
  emits: ["refresh"],
  components: { DetailEstimation, OverlayPanel, Skeleton },
  watch: {
    isShow: "syncDetailEstimationVisibility"
  }
})
class TotalTarifSectionV4 extends Vue.with(Props) {
  isShow = false;
  alignmentFrame = 0;

  get detailEstimationOverlay(): OverlayPanelRef {
    return this.$refs.detailEstimationOverlay as OverlayPanelRef;
  }

  mounted(): void {
    document.addEventListener("click", this.onDocumentClick);
  }

  beforeUnmount(): void {
    document.removeEventListener("click", this.onDocumentClick);
    window.cancelAnimationFrame(this.alignmentFrame);
  }

  get chevronIcon(): string {
    return this.isShow ? ChevronUp : ChevronDown;
  }

  get title(): string {
    return this.$t(
      this.isCodOrDfod
        ? "bookingV4.footer.recipientPayable"
        : "bookingV4.footer.totalTariff"
    ) as string;
  }

  get isCod(): boolean {
    return this.paymentMethod.method === PaymentMethodV4.COD;
  }

  get isDfod(): boolean {
    return this.paymentMethod.method === PaymentMethodV4.DFOD;
  }

  get isCodOrDfod(): boolean {
    return this.isCod || this.isDfod;
  }

  get resolvedCodCurrencyPrefix(): string {
    return this.codCurrencyPrefix || this.currencyPrefix;
  }

  get resolvedCodCurrencyCode(): string {
    return (
      this.codCurrencyCode || (this.currencyPrefix === "RM" ? "MYR" : "IDR")
    );
  }

  get displayPrice(): string {
    if (this.isPriceMasked) {
      const prefix = this.isCodOrDfod
        ? this.resolvedCodCurrencyPrefix
        : this.currencyPrefix;
      return `${prefix}*******`;
    }
    if (this.isCodOrDfod) return this.currencyCod(this.finalValue);
    return this.currency(this.finalValue);
  }

  get finalValue(): number {
    if (this.isCodOrDfod) return this.estimationPrice.codAmount;
    return this.estimationPrice.finalPrice;
  }

  get originalValue(): number {
    return this.estimationPrice.totalTariff;
  }

  get promoDiscounts(): DetailDiscount[] {
    const discounts = Array.isArray(this.estimationPrice.listDiscount)
      ? this.estimationPrice.listDiscount
      : [];
    return discounts.filter((item: DetailDiscount) =>
      ["tactical", "campaign"].includes(item.pdcCategory)
    );
  }

  /*
  get hasVoucherDiscount(): boolean {
    return Boolean(
      this.appliedVoucherCode && this.estimationPrice.voucherDiscountAmount > 0
    );
  }
  */

  get hasAnyDiscount(): boolean {
    return this.promoDiscounts.length > 0;
  }

  get showOriginalStrikethrough(): boolean {
    return (
      !this.isCodOrDfod &&
      !this.isPriceMasked &&
      this.hasAnyDiscount &&
      this.originalValue > this.finalValue
    );
  }

  get combinedPromoDiscountLabel(): string {
    const percentages = this.promoDiscounts
      .map((item: DetailDiscount) => item.pdcPromoDiscount)
      .filter((value: number | string) => Boolean(value))
      .map((value: number | string) => `${value}%`);
    if (!percentages.length) return "";
    return this.$t("bookingV4.footer.discountLabel", {
      value: percentages.join(" + ")
    }) as string;
  }

  showDetail(): void {
    if (this.canOpenDetail) this.isShow = !this.isShow;
  }

  syncDetailEstimationVisibility(isShow: boolean): void {
    if (!isShow) {
      this.detailEstimationOverlay.hide();
      return;
    }

    const target = this.detailEstimationTarget;
    this.detailEstimationOverlay.show({ currentTarget: target });
    this.$nextTick(() => this.scheduleOverlayAlignment(target));
  }

  get detailEstimationTarget(): HTMLElement {
    const trigger = this.$refs.detailEstimationTrigger as HTMLElement;
    return trigger.closest<HTMLElement>(".booking-v4-sticky-footer") || trigger;
  }

  scheduleOverlayAlignment(target: HTMLElement): void {
    window.cancelAnimationFrame(this.alignmentFrame);
    this.alignmentFrame = window.requestAnimationFrame(() =>
      this.alignOverlayToBottomLeft(target)
    );
  }

  alignOverlayToBottomLeft(target: HTMLElement): void {
    const overlay = this.detailEstimationOverlay.container;
    if (!overlay) return;

    const targetRect = target.getBoundingClientRect();
    const overlayRect = overlay.getBoundingClientRect();
    const left = overlay.offsetLeft + targetRect.left - overlayRect.right + 8;
    const top = overlay.offsetTop + targetRect.bottom - overlayRect.bottom - 24;
    overlay.style.left = `${left}px`;
    overlay.style.top = `${top}px`;
  }

  onDocumentClick(event: MouseEvent): void {
    if (!this.isShow) return;

    const target = event.target as Node;
    const overlay = this.detailEstimationOverlay.container;
    if (
      (this.$el as HTMLElement).contains(target) ||
      overlay?.contains(target)
    ) {
      return;
    }

    this.isShow = false;
  }

  formatCompactPrice(value: number) {
    if (value >= 1000000) {
      return `${this.currencyPrefix}${this.$t(
        "bookingV4.voucher.card.million",
        {
          value: Math.floor(value / 1000000)
        }
      )}`;
    }

    if (value >= 1000) {
      return `${this.currencyPrefix}${this.$t(
        "bookingV4.voucher.card.thousand",
        {
          value: Math.floor(value / 1000)
        }
      )}`;
    }

    return value.toString();
  }
  currency(value: number): string {
    return this.currencyPrefix === "RM"
      ? formatPriceRM(value || 0)
      : formatPriceRP(value || 0);
  }

  // COD Amount / Biaya COD render in destination currency for cross-country
  // COD (GENESIS-21349); Total Tarif above always stays origin currency via
  // currency(). Falls back to the same currency as currency() when
  // codCurrencyCode isn't set (domestic — zero regression).
  currencyCod(value: number): string {
    if (this.isCrossCountryCod) {
      const hasFraction = (num: number) => {
        return typeof num === 'number' && !Number.isInteger(num);
      }
      const codAmount = formatByCountry(value, {
        type: "money",
        country: this.modelValue.receiver.destinationCountry.code,
        fractionDigits: hasFraction(value) ? 2 : 0,
        minFractionDigits: hasFraction(value) ? 2 : 0
      });

      return `${this.modelValue.receiver.destinationCountry.currencySymbol}${codAmount}`;
    }

    const config = configPrice[this.resolvedCodCurrencyCode] ?? configPrice.IDR;
    if (!value) return `${config.prefix}0`;
    return `${config.prefix}${value.toLocaleString(config.locales)}`;
  }

  onKeydownTrigger(e: any) {
    if (!this.canOpenDetail) return;

    if (e.key === "Escape" || e.key === "ArrowDown") {
      this.isShow = false;
      e.preventDefault();
      return;
    }

    if (e.key === "ArrowUp") {
      this.isShow = true;
      e.preventDefault();
      return;
    }
  }
}

export default TotalTarifSectionV4;
